import { MetricDataPoint } from '../services/grafana';

/**
 * Bot telemetry freshness helpers (CAO-180).
 *
 * Bots push telemetry through the OTel collector, which runs with
 * `metric_expiration: 20s` (deploy/monitoring/config/otel-collector.yaml): a bot's
 * series stops being exported 20s after its last sample, and Prometheus then drops
 * it. Anything answering "which bots are up right now" must therefore look back no
 * further than that window.
 *
 * The stale-value bug this module fixes: the Bots-IP / Region panel used
 * `last_over_time(<metric>[$__range])` (Grafana) / `[<timeRange>s]` (this app), a
 * lookback spanning the whole dashboard range. A bot removed mid-window kept its
 * last sample — and therefore its row — for the rest of the range instead of
 * falling off once it stopped reporting.
 */
export const TELEMETRY_FRESHNESS_SEC = 20;

/** The counter every bot always emits; used as the liveness signal. */
export const BOT_TELEMETRY_METRIC = 'network_bandwidth_down_bytes_total';

/**
 * PromQL for the Bots-IP / Region rows.
 *
 * With no `lookbackSeconds` (the live view) the lookback is the freshness window,
 * so a bot disappears from the table one poll after it stops reporting.
 *
 * `lookbackSeconds` is only for an explicit, closed time range — "which bots took
 * part in this window" — and must be paired with an evaluation timestamp at the end
 * of that range (see `queryPrometheusInstantVector`'s `timeSec`). Passing a wide
 * lookback while evaluating at "now" is what produced the stale rows.
 */
export const buildBotsIpRegionQuery = (
  selector: string,
  lookbackSeconds?: number | null
): string => {
  const requested =
    typeof lookbackSeconds === 'number' && Number.isFinite(lookbackSeconds)
      ? Math.floor(lookbackSeconds)
      : TELEMETRY_FRESHNESS_SEC;
  const windowSeconds = Math.max(TELEMETRY_FRESHNESS_SEC, requested);
  return (
    `max by (provider,public_ip,public_ipv6,region) ` +
    `(last_over_time(${BOT_TELEMETRY_METRIC}${selector}[${windowSeconds}s]))`
  );
};

/**
 * PromQL for the per-provider bot count: one point per provider counting the bots
 * whose most recent sample is inside the freshness window. A bot that stopped
 * reporting contributes 0 (its series is gone), never its last known value.
 */
export const buildBotsByProviderQuery = (selector: string): string =>
  `sum by (cloud_provider, provider) (max by (provider, public_ip, public_ipv6, region, cloud_provider) ` +
  `((time() - timestamp(${BOT_TELEMETRY_METRIC}${selector})) < bool ${TELEMETRY_FRESHNESS_SEC}))`;

/**
 * When a bot stops reporting its series DISAPPEARS from Prometheus, so
 * `sum by (provider)` returns a gap rather than a 0. A range query therefore ends
 * at the last non-zero value and the provider line freezes at the old bot count
 * instead of dropping to zero — the "old value remains on graphs" symptom.
 *
 * Seeded range results have no live "now" tick to correct this (only the
 * incremental append path synthesizes zeros), so backfill an explicit 0 at `endMs`
 * for every provider whose latest sample is older than the freshness window. Uses
 * the same window as the query, with step slack so an actively-reporting provider
 * on a coarse range step is never zeroed.
 */
export const zeroFillStaleProviders = (
  map: Record<string, MetricDataPoint[]>,
  endMs: number,
  stepSeconds: number
): Record<string, MetricDataPoint[]> => {
  const staleAfterMs = Math.max(TELEMETRY_FRESHNESS_SEC, 2 * stepSeconds) * 1000;
  const next: Record<string, MetricDataPoint[]> = {};
  Object.entries(map).forEach(([provider, points]) => {
    if (points.length === 0) {
      next[provider] = points;
      return;
    }
    const lastTs = points[points.length - 1].timestamp;
    if (endMs - lastTs <= staleAfterMs) {
      next[provider] = points;
      return;
    }
    const markMs = Math.min(endMs, lastTs + staleAfterMs);
    next[provider] =
      markMs < endMs
        ? [...points, { timestamp: markMs, value: 0 }, { timestamp: endMs, value: 0 }]
        : [...points, { timestamp: endMs, value: 0 }];
  });
  return next;
};
